import React from 'react';

// Accordion — react-bootstrap-compatible: <Accordion defaultActiveKey flush alwaysOpen>
// <Accordion.Item eventKey><Accordion.Header/><Accordion.Body/></Accordion.Item>.
const AccCtx = React.createContext<{ open: Set<string>; toggle: (k: string) => void }>({ open: new Set(), toggle: () => {} });
const ItemCtx = React.createContext<string>('');

interface AccordionType extends React.FC<{
  defaultActiveKey?: string | string[];
  alwaysOpen?: boolean;
  flush?: boolean;
  className?: string;
  children?: React.ReactNode;
}> {
  Item: React.FC<{ eventKey: string; className?: string; children?: React.ReactNode }>;
  Header: React.FC<{ className?: string; as?: React.ElementType; children?: React.ReactNode }>;
  Body: React.FC<{ className?: string; children?: React.ReactNode }>;
}

export const Accordion = (({ defaultActiveKey, alwaysOpen, flush, className = '', children }) => {
  const [open, setOpen] = React.useState<Set<string>>(
    () => new Set(Array.isArray(defaultActiveKey) ? defaultActiveKey : defaultActiveKey != null ? [defaultActiveKey] : []),
  );
  const toggle = (k: string) =>
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else {
        if (!alwaysOpen) n.clear();
        n.add(k);
      }
      return n;
    });
  return (
    <AccCtx.Provider value={{ open, toggle }}>
      <div className={`${flush ? '' : 'tw-rounded tw-border tw-border-line tw-divide-y tw-divide-line'} ${className}`}>{children}</div>
    </AccCtx.Provider>
  );
}) as AccordionType;

Accordion.Item = ({ eventKey, className = '', children }) => (
  <ItemCtx.Provider value={eventKey}>
    <div className={className}>{children}</div>
  </ItemCtx.Provider>
);

Accordion.Header = ({ as, className = '', children }) => {
  const { open, toggle } = React.useContext(AccCtx);
  const k = React.useContext(ItemCtx);
  const isOpen = open.has(k);
  const Comp: React.ElementType = as || 'button';
  const extra = Comp === 'button' ? { type: 'button' as const } : {};
  return (
    <Comp
      onClick={() => toggle(k)}
      aria-expanded={isOpen}
      className={`tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-bg-surface-alt tw-px-4 tw-py-3 tw-text-left hover:tw-bg-surface-hover ${className}`}
      {...extra}
    >
      <span>{children}</span>
      <i className={`bi bi-chevron-down tw-transition ${isOpen ? 'tw-rotate-180' : ''}`} />
    </Comp>
  );
};

Accordion.Body = ({ className = '', children }) => {
  const { open } = React.useContext(AccCtx);
  const k = React.useContext(ItemCtx);
  if (!open.has(k)) return null;
  return <div className={`tw-border-t tw-border-line tw-px-4 tw-py-3 ${className}`}>{children}</div>;
};

export default Accordion;
